import React from 'react';
import {
  Card,
  CardBody,
  CardHeader,
  Button,
  Badge,
} from 'reactstrap';
import { useTranslation } from 'react-i18next';
import {
  CompanyDocumentDto,
  PartnerDocumentDto,
  IndividualDocumentDto,
  DocumentOwnerType,
  getDocumentTypeLabel,
  getDocumentStatusLabel,
  getDocumentStatusColor,
  getFileIcon,
  formatFileSize,
  formatDateTime,
} from '../../../../types/documents';

interface DocumentPreviewPanelProps {
  document: CompanyDocumentDto | PartnerDocumentDto | IndividualDocumentDto | null;
  ownerType: DocumentOwnerType;
  isExpanded?: boolean;
  onClose: () => void;
  onToggleExpand?: () => void;
  onDownload?: () => void;
  onReplace?: () => void;
  onDelete?: () => void;
  isAdmin?: boolean;
}

const DocumentPreviewPanel: React.FC<DocumentPreviewPanelProps> = ({
  document,
  ownerType,
  isExpanded = false,
  onClose,
  onToggleExpand,
  onDownload,
  onReplace,
  onDelete,
  isAdmin = false,
}) => {
  const { t } = useTranslation();

  if (!document) {
    return null;
  }

  const fileIcon = getFileIcon(document.contentType, document.originalFileName);
  const statusLabel = getDocumentStatusLabel(document.status);
  const statusColor = getDocumentStatusColor(document.status);

  // Check if the file can be previewed
  const canPreview =
    document.contentType === 'application/pdf' ||
    document.contentType.startsWith('image/');

  return (
    <Card className="position-sticky" style={{ top: '20px' }}>
      <CardHeader className="bg-primary-subtle">
        <div className="d-flex justify-content-between align-items-center">
          <h6 className="mb-0">
            <i className="ri-eye-line me-2"></i>
            {t('Visualização do Documento')}
          </h6>
          <div>
            {onToggleExpand && (
              <Button
                color="ghost-secondary"
                size="sm"
                className="btn-icon me-1"
                onClick={onToggleExpand}
                title={isExpanded ? t('Reduzir') : t('Expandir')}
              >
                <i className={isExpanded ? 'ri-contract-left-right-line' : 'ri-expand-left-right-line'}></i>
              </Button>
            )}
            <Button
              color="ghost-secondary"
              size="sm"
              className="btn-icon"
              onClick={onClose}
            >
              <i className="ri-close-line"></i>
            </Button>
          </div>
        </div>
      </CardHeader>

      <CardBody>
        {/* Document Info */}
        <div className="mb-3">
          <h6 className="mb-2 text-break" title={document.originalFileName}>
            {document.originalFileName}
          </h6>
          <div>
            <Badge color={statusColor} className="me-1">
              {statusLabel}
            </Badge>
            {document.version > 1 && (
              <Badge color="info" className="me-1">
                v{document.version}
              </Badge>
            )}
            {document.isDeleted && (
              <Badge color="danger">{t('Excluído')}</Badge>
            )}
          </div>
        </div>

        {/* Preview Area */}
        {canPreview && document.signedUrl && !document.isDeleted ? (
          <div className="mb-3">
            <div
              className="border rounded"
              style={{
                height: isExpanded ? '70vh' : '300px',
                overflow: 'auto',
                backgroundColor: '#f8f9fa'
              }}
            >
              {document.contentType === 'application/pdf' ? (
                <iframe
                  src={document.signedUrl}
                  title={document.originalFileName}
                  width="100%"
                  height="100%"
                  style={{ border: 'none' }}
                />
              ) : document.contentType.startsWith('image/') ? (
                <img
                  src={document.signedUrl}
                  alt={document.originalFileName}
                  className="img-fluid"
                  style={{ maxHeight: '100%', width: '100%', objectFit: 'contain' }}
                />
              ) : null}
            </div>
          </div>
        ) : (
          <div className="text-center py-4 mb-3 bg-light rounded">
            <i className={`${fileIcon} fs-1 text-muted`}></i>
            <p className="text-muted mt-2 mb-0">
              {document.isDeleted
                ? t('Documento excluído')
                : t('Visualização não disponível para este tipo de arquivo')}
            </p>
          </div>
        )}

        {/* Action Buttons */}
        {!document.isDeleted && (
          <div className="d-grid gap-2 mb-3">
            {onDownload && (
              <Button color="primary" size="sm" onClick={onDownload}>
                <i className="ri-download-2-line me-1"></i>
                {t('Baixar Documento')}
              </Button>
            )}
            {document.signedUrl && canPreview && (
              <Button
                color="secondary"
                outline
                size="sm"
                onClick={() => window.open(document.signedUrl, '_blank')}
              >
                <i className="ri-external-link-line me-1"></i>
                {t('Abrir em Nova Aba')}
              </Button>
            )}
          </div>
        )}

        {/* Admin Actions */}
        {isAdmin && (
          <div className="d-grid gap-2 mb-3">
            {onReplace && !document.isDeleted && (
              <Button color="info" outline size="sm" onClick={onReplace}>
                <i className="ri-refresh-line me-1"></i>
                {t('Substituir')}
              </Button>
            )}
            {onDelete && !document.isDeleted && (
              <Button color="danger" outline size="sm" onClick={onDelete}>
                <i className="ri-delete-bin-line me-1"></i>
                {t('Excluir')}
              </Button>
            )}
          </div>
        )}

        {/* Document Details */}
        <div className="border-top pt-3">
          <h6 className="fs-13 text-uppercase mb-3">{t('Detalhes')}</h6>
          <table className="table table-sm table-borderless mb-0">
            <tbody>
              <tr>
                <td className="fw-medium text-muted" style={{ width: '40%' }}>
                  {t('Tipo')}
                </td>
                <td>{getDocumentTypeLabel(document.documentType, ownerType)}</td>
              </tr>
              <tr>
                <td className="fw-medium text-muted">{t('Tamanho')}</td>
                <td>{formatFileSize(document.fileSize)}</td>
              </tr>
              <tr>
                <td className="fw-medium text-muted">{t('Versão')}</td>
                <td>v{document.version}</td>
              </tr>
              <tr>
                <td className="fw-medium text-muted">{t('Formato')}</td>
                <td>{document.contentType}</td>
              </tr>
              <tr>
                <td className="fw-medium text-muted">{t('Enviado em')}</td>
                <td>{formatDateTime(document.createdAt)}</td>
              </tr>
              {document.expirationDate && (
                <tr>
                  <td className="fw-medium text-muted">{t('Expira em')}</td>
                  <td>{formatDateTime(document.expirationDate)}</td>
                </tr>
              )}
              {document.notes && (
                <tr>
                  <td className="fw-medium text-muted" style={{ verticalAlign: 'top' }}>
                    {t('Observações')}
                  </td>
                  <td style={{ whiteSpace: 'pre-wrap' }}>{document.notes}</td>
                </tr>
              )}
              {document.updatedAt && (
                <tr>
                  <td className="fw-medium text-muted">{t('Última Atualização')}</td>
                  <td>{formatDateTime(document.updatedAt)}</td>
                </tr>
              )}
              {document.isDeleted && document.deletedAt && (
                <tr>
                  <td className="fw-medium text-muted">{t('Excluído em')}</td>
                  <td>{formatDateTime(document.deletedAt)}</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </CardBody>
    </Card>
  );
};

export default DocumentPreviewPanel;
